import { useState } from "react";
import { useLocation, useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Download, Loader2, Pencil } from "lucide-react";
import { StudentCopyRender } from "@/components/StudentCopyRender";
import { DownloadTestDialog } from "@/components/DownloadTestDialog";
import { readingLevelDisplayLabel, type Test } from "@shared/schema";

// Preview surface for a saved test. Reachable via /tests/:id from the
// Dashboard's Open action. Shows a summary card on top (name, stats,
// topics, Download + Edit Items buttons) and a read-only student-copy
// render below — no progress bar, no edit affordances, no TestContext
// involvement. The in-progress workflow draft (if any) stays untouched.

export default function TestPreview() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const [showDownload, setShowDownload] = useState(false);

  // queryKey is the path that the global queryFn will fetch (Bearer token
  // is injected by queryClient). On 404/403 we get a thrown error and
  // surface a friendly card.
  const { data: test, isLoading, error } = useQuery<Test>({
    queryKey: [`/api/tests/${params.id}`],
    enabled: !!params.id,
  });

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      );
    }

    if (error || !test) {
      return (
        <Card>
          <CardContent className="py-12 text-center space-y-4">
            <p className="text-muted-foreground">
              We couldn&rsquo;t load this test. It may have been deleted or you don&rsquo;t have access.
            </p>
            <Button variant="outline" onClick={() => setLocation("/dashboard")}>
              Back to My Tests
            </Button>
          </CardContent>
        </Card>
      );
    }

    const subject = test.config?.subject;
    const readingLevel = test.config?.readingLevel;
    const topics = test.blueprint?.topics ?? [];

    return (
      <>
        <Card className="mb-6">
          <CardContent className="pt-6 space-y-6">
            <div>
              <h1 className="text-3xl font-bold mb-1" data-testid="text-test-name">
                {test.name}
              </h1>
              <p className="text-sm text-muted-foreground">
                Created {new Date(test.createdAt).toLocaleDateString()}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-6 py-4 border-y">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Total questions</p>
                <p className="text-3xl font-bold text-primary">{test.questions.length}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Subject</p>
                <p className="text-lg font-semibold capitalize">{subject || "—"}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Reading level</p>
                <p className="text-lg font-semibold">{readingLevel ? readingLevelDisplayLabel(readingLevel) : "—"}</p>
              </div>
            </div>

            {topics.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2">Topics</h3>
                <ul className="space-y-1 text-sm">
                  {topics.map((topic, idx) => {
                    const count = test.questions.filter((q) => q.topicId === topic.id).length;
                    return (
                      <li key={topic.id}>
                        {idx + 1}. {topic.name}{" "}
                        <span className="text-muted-foreground">
                          ({count} question{count === 1 ? "" : "s"})
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => setShowDownload(true)}
                data-testid="button-download-preview"
              >
                Download <Download className="w-4 h-4" />
              </Button>
              <Button
                variant="secondary"
                onClick={() => setLocation(`/tests/${test.id}/edit`)}
                data-testid="button-edit-items"
              >
                Edit Test <Pencil className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <StudentCopyRender
          name={test.name}
          directions={test.directions}
          questions={test.questions}
        />

        <DownloadTestDialog
          open={showDownload}
          onOpenChange={setShowDownload}
          test={test}
        />
      </>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header pageTitle="Test" />
      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setLocation("/dashboard")}
          className="mb-4 -ml-2"
          data-testid="button-back-to-tests"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to My Tests
        </Button>
        {renderBody()}
      </main>
    </div>
  );
}
